import React, { useState, useEffect } from 'react';
import {
  Container, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography, Chip, IconButton, Box,
  Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControl, InputLabel, Select, MenuItem,
  Card, CardContent, useMediaQuery, useTheme
} from '@mui/material';
import { Delete, Add, Lock, Edit } from '@mui/icons-material';
import { userService } from '../services/userService';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import api from '../services/api';

const Users = () => {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const [users, setUsers] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editData, setEditData] = useState({ username: '', email: '', role: '' });
  const [newPassword, setNewPassword] = useState('');
  const [newUser, setNewUser] = useState({
    username: '',
    email: '',
    password: '',
    role: 'user'
  });
  const { user: currentUser, setUser: setCurrentUserData } = useAuth();

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const data = await userService.getAllUsers();
      setUsers(data);
    } catch (error) {
      toast.error('Benutzer konnten nicht geladen werden');
    }
  };

  const handleDelete = async (id) => {
    if (id === currentUser.id) {
      toast.error('Sie können Ihr eigenes Konto nicht löschen');
      return;
    }

    if (window.confirm('Möchten Sie diesen Benutzer wirklich löschen?')) {
      try {
        await userService.deleteUser(id);
        toast.success('Benutzer erfolgreich gelöscht');
        loadUsers();
      } catch (error) {
        toast.error('Benutzer konnte nicht gelöscht werden');
      }
    }
  };

  const getRoleColor = (role) => {
    const colors = {
      admin: 'error',
      technician: 'warning',
      user: 'primary',
      guest: 'default'
    };
    return colors[role] || 'default';
  };

  const getRoleLabel = (role) => {
    const labels = {
      admin: 'Admin',
      technician: 'Techniker',
      user: 'Benutzer',
      guest: 'Gast'
    };
    return labels[role] || role;
  };

  const handleCreateUser = async () => {
    try {
      if (!newUser.username || !newUser.email || !newUser.password) {
        toast.error('Bitte füllen Sie alle Pflichtfelder aus');
        return;
      }

      await userService.createUser(newUser);
      toast.success('Benutzer erfolgreich erstellt');
      setOpenDialog(false);
      setNewUser({ username: '', email: '', password: '', role: 'user' });
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Benutzer konnte nicht erstellt werden');
    }
  };

  const handleOpenPasswordDialog = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setPasswordDialogOpen(true);
  };

  const handleChangePassword = async () => {
    if (!newPassword || newPassword.length < 4) {
      toast.error('Passwort muss mindestens 4 Zeichen lang sein');
      return;
    }
    try {
      await api.put(`/users/${selectedUser.id}/password`, { newPassword });
      toast.success(`Passwort für ${selectedUser.username} erfolgreich geändert`);
      setPasswordDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Passwort konnte nicht geändert werden');
    }
  };

  const handleOpenEditDialog = (user) => {
    setSelectedUser(user);
    setEditData({ username: user.username, email: user.email, role: user.role });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    try {
      const response = await api.put(`/users/${selectedUser.id}`, editData);
      toast.success(`Benutzer ${editData.username} erfolgreich aktualisiert`);
      // Eigene Daten im AuthContext aktualisieren
      if (selectedUser.id === currentUser.id) {
        setCurrentUserData(prev => ({ ...prev, ...response.data }));
      }
      setEditDialogOpen(false);
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Benutzer konnte nicht aktualisiert werden');
    }
  };

  return (
    <Container maxWidth="lg">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" sx={{ fontWeight: 700, background: 'linear-gradient(135deg, #CC0000 0%, #8B0000 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
          Benutzer
        </Typography>
        {currentUser.role === 'admin' && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={() => setOpenDialog(true)}
          >
            Benutzer hinzufügen
          </Button>
        )}
      </Box>

      {isMobile ? (
        <Box display="flex" flexDirection="column" gap={1.5}>
          {users.map((user) => (
            <Card key={user.id} sx={user.role === 'guest' ? { border: '1px solid rgba(255, 152, 0, 0.3)' } : {}}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box flex={1} minWidth={0}>
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>{user.username}</Typography>
                      <Chip label={getRoleLabel(user.role)} color={getRoleColor(user.role)} size="small" variant={user.role === 'guest' ? 'outlined' : 'filled'} />
                    </Box>
                    <Typography variant="body2" color="text.secondary" noWrap>{user.email}</Typography>
                    <Typography variant="caption" color="text.secondary">seit {new Date(user.created_at).toLocaleDateString('de-DE')}</Typography>
                  </Box>
                  {currentUser.role === 'admin' && (
                    <Box display="flex">
                      <IconButton size="small" onClick={() => handleOpenEditDialog(user)} color={user.role === 'guest' ? 'warning' : 'default'}><Edit fontSize="small" /></IconButton>
                      <IconButton size="small" onClick={() => handleOpenPasswordDialog(user)}><Lock fontSize="small" /></IconButton>
                      {user.id !== currentUser.id && (
                        <IconButton size="small" onClick={() => handleDelete(user.id)} color="error"><Delete fontSize="small" /></IconButton>
                      )}
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          ))}
          {users.length === 0 && (
            <Paper sx={{ p: 3, textAlign: 'center' }}><Typography color="textSecondary">Keine Benutzer gefunden</Typography></Paper>
          )}
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>E-Mail</TableCell>
                <TableCell>Rolle</TableCell>
                <TableCell>Erstellt am</TableCell>
                <TableCell>Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id} sx={user.role === 'guest' ? { backgroundColor: 'rgba(255, 152, 0, 0.05)' } : {}}>
                  <TableCell>#{user.id}</TableCell>
                  <TableCell>{user.username}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Chip label={getRoleLabel(user.role)} color={getRoleColor(user.role)} size="small" variant={user.role === 'guest' ? 'outlined' : 'filled'} />
                  </TableCell>
                  <TableCell>{new Date(user.created_at).toLocaleDateString('de-DE')}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {currentUser.role === 'admin' && (
                      <>
                        <IconButton size="small" onClick={() => handleOpenEditDialog(user)} title="Benutzer bearbeiten" color={user.role === 'guest' ? 'warning' : 'default'}><Edit /></IconButton>
                        <IconButton size="small" onClick={() => handleOpenPasswordDialog(user)} title="Passwort ändern"><Lock /></IconButton>
                        {user.id !== currentUser.id && (
                          <IconButton size="small" onClick={() => handleDelete(user.id)} title="Benutzer löschen" color="error"><Delete /></IconButton>
                        )}
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {users.length === 0 && (
            <Box p={3} textAlign="center"><Typography color="textSecondary">Keine Benutzer gefunden</Typography></Box>
          )}
        </TableContainer>
      )}

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Neuen Benutzer erstellen</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              label="Name"
              value={newUser.username}
              onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="E-Mail"
              type="email"
              value={newUser.email}
              onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Passwort"
              type="password"
              value={newUser.password}
              onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
              fullWidth
              required
            />
            <FormControl fullWidth>
              <InputLabel>Rolle</InputLabel>
              <Select
                value={newUser.role}
                onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                label="Rolle"
              >
                <MenuItem value="user">Benutzer</MenuItem>
                <MenuItem value="technician">Techniker</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Abbrechen</Button>
          <Button onClick={handleCreateUser} variant="contained" color="primary">
            Benutzer erstellen
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Passwort ändern — {selectedUser?.username}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="normal"
            label="Neues Passwort"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialogOpen(false)}>Abbrechen</Button>
          <Button onClick={handleChangePassword} variant="contained">
            Passwort speichern
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Benutzer bearbeiten — {selectedUser?.username}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              label="Name"
              value={editData.username}
              onChange={(e) => setEditData({ ...editData, username: e.target.value })}
              fullWidth
            />
            <TextField
              label="E-Mail"
              type="email"
              value={editData.email}
              onChange={(e) => setEditData({ ...editData, email: e.target.value })}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Rolle</InputLabel>
              <Select
                value={editData.role}
                onChange={(e) => setEditData({ ...editData, role: e.target.value })}
                label="Rolle"
              >
                <MenuItem value="guest">Gast (nicht freigeschaltet)</MenuItem>
                <MenuItem value="user">Benutzer</MenuItem>
                <MenuItem value="technician">Techniker</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Abbrechen</Button>
          <Button onClick={handleSaveEdit} variant="contained">
            Speichern
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Users;
